import { httpRequest } from '../../shared/lib/utils/httpClient';
import { rowsPerPage } from '../../pages/constants';

export interface PartnerList {
  totalRecords: number;
  partnerList: Partner[];
}

export interface Partner {
  id: number;
  partnerBusinessId: string | null;
  partnerName: string | null;
  alias: string | null;
  facilityCount: number;
  website: string | null;
  status: string | null;
  partnerTypeName: string | null;
}

export interface ServicesProvided {
  id: number | string;
  name: string;
}

interface PartnerType {
  id: number;
  name: string;
}

interface AssignedTo {
  id: number;
  name: string;
}

export interface PartnerMetaDate {
  servicesProvided: ServicesProvided[];
  partnerTypes: PartnerType[];
  assignedTo: AssignedTo[];
  usStates: string[];
}

export interface PartnerData {
  partnerInfo: PartnerInfo;
  contactDetails: ContactDetails;
  itSystemDetails: ItSystemDetails;
  address: Address;
  // billingAddress: BillingAddress;
  description: string;
}

// ----------------------------
// partnerInfo
// ----------------------------
export interface PartnerInfo {
  id: number;
  name: string;
  partnerBusinessId: string;
  servicesProvided: ServicesProvided[];
  alias: string;
  partnerTypeId: number;
  website: string;
  activePartnerSince: string;
  terminationDate: string;
  assignedTo: number;
  status: boolean;
}

// ----------------------------
// contactDetails
// ----------------------------
export interface ContactDetails {
  id: number;
  primaryPhone: string;
  itHelpDeskEmail: string;
  itHelpDeskPhone: string;
  fax: string;
  contactEmail: string;
  otherEmail: string;
  faxDescription: string;
  otherEmailDescription: string;
}

// ----------------------------
// itSystemDetails
// ----------------------------
export interface ItSystemDetails {
  id: number;
  networkAccessType: string;
  telemedicinePlatform: string;
  networkAccessUrl: string;
  telemedicinePlatformUrl: string;
  vpnGatewaySoftware: string;
  trainingEquipment: string;
  emr: string;
  emailDomainPrimary: string;
  documentationIn: string;
  emailDomainSecondary: string;
  mfa: string;
  emailDomainTertiary: string;
  devicesUtilized: string;
  itNotes: string;
}

// ----------------------------
// physicalAddress
// ----------------------------
export interface Address {
  id: number;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  county: string;
}

// ----------------------------
// billingAddress
// ----------------------------
export interface BillingAddress {
  id: number;
  billingAddressLine1: string;
  billingAddressLine2: string;
  billingCity: string;
  billingState: string;
  billingPostalCode: string;
  billingCounty: string;
}

export const getPartnersList = async (
  userId: string,
  page: number = 0,
  size: number = rowsPerPage,
  sortField?: string,
  sortType?: string,
  searchTerm?: string,
  statusId?: number[]
): Promise<PartnerList> => {
  const params: Record<string, string | number> = {
    page,
    size,
  };

  if (sortField) params.sortField = sortField;
  if (sortType) params.sortType = sortType;
  if (searchTerm) params.searchTerm = searchTerm;
  if (statusId && statusId.length > 0) {
    params.statusId = statusId.join(',');
  }

  const response = await httpRequest<PartnerList>({
    method: 'GET',
    url: '/operation/partners',
    params,
    headers: { userId },
  });
  return response;
};

export const getPartnerMetaData = async (
  userId: string
): Promise<PartnerMetaDate> => {
  const response = await httpRequest<PartnerMetaDate>({
    method: 'GET',
    url: `/operation/partner/metadata`,
    headers: { userId },
  });
  return response;
};

export const saveOrUpdatePartner = async (
  userId: string,
  action: string,
  payload: PartnerData
) => {
  const apiAction = action === 'Save' ? 'SAVE' : 'UPDATE';
  const response = await httpRequest<PartnerData>({
    method: 'PUT',
    url: `/operation/partner?action=${apiAction}`,
    headers: { userId },
    data: payload,
  });
  return response;
};

export const getPartnerDetailsById = async (
  userId: string,
  partnerId: number
): Promise<PartnerData> => {
  const response = await httpRequest<PartnerData>({
    method: 'GET',
    url: `/operation/partner?partnerId=${partnerId}`,
    headers: { userId },
  });
  return response;
};
